import React from 'react';
// 导入国际化钩子
import { useTranslation } from '@/i18n';

// 定义基础对话框的props接口
interface BaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  width?: number;
  height?: number;
}

/**
 * 基础对话框组件
 * - 800x600px默认尺寸
 * - 背景样式参考inputBar
 * - 支持自定义尺寸和标题
 * - 作为其他对话框组件的基类
 */
const BaseDialog: React.FC<BaseDialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = 800,
  height = 600
}) => {
  // 如果对话框未打开，不渲染任何内容
  if (!isOpen) return null;
  
  // 国际化钩子
  const { t } = useTranslation();

  return (
    // 背景遮罩层
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50"
      onClick={onClose} // 点击遮罩层关闭对话框
    >
      {/* 对话框容器 */}
      <div
        className="relative bg-gradient-to-r from-blue-500/20 via-purple-500/20 to-pink-500/20 backdrop-blur-md rounded-2xl p-4 shadow-2xl overflow-hidden"
        style={{ width, height }}
        onClick={(e) => e.stopPropagation()} // 阻止事件冒泡，避免点击对话框内部关闭对话框
      >
        {/* 对话框头部 */}
        {title && (
          <div className="flex items-center justify-between mb-4 border-b border-gray-700/50 pb-2">
            <h2 className="text-xl font-semibold text-white">{title}</h2>
            <button
              className="text-gray-300 hover:text-white transition-colors"
              onClick={onClose}
              title={t('common.closeDialog')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        )}
        {/* 对话框内容区域 */}
        <div className="bg-gray-800/95 backdrop-blur-sm rounded-xl p-4 h-[calc(100%-72px)] overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  );
};

export default BaseDialog;
export type { BaseDialogProps };